/**
 * Scheduled Email Report Config – Control Center
 * OMS API: GET/PUT merchant-config (MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS).
 * Request/response snake_case. Column selection is in Report Configuration.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { API } from "../../utils/apiUrls";
import { OrderBusinessStatusEnum } from "../../utils/enums";
import {
  Plus,
  Trash2,
  Save,
  Mail,
  Clock,
  Calendar,
  FileBarChart2,
  ChevronDown,
  ChevronUp,
  Info,
  Check,
  X,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LOTTIE_LOADER = "/animations/loader.lottie";
const CONFIG_KEY = "MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS";

const REPORT_TYPES = [
  { id: "pickup_pending", label: "Pickup Pending Report", description: "Shipments not yet picked up" },
  { id: "delivery", label: "Delivery Report", description: "Shipments delivered" },
  { id: "delivery_failed", label: "Delivery Failed Report", description: "Shipments with delivery failed" },
];

const FREQUENCIES = [
  { id: "DAILY", label: "Daily" },
  { id: "WEEKLY", label: "Weekly" },
  { id: "MONTHLY", label: "Monthly" },
];

const STATUS_OPTIONS = Object.entries(OrderBusinessStatusEnum).map(([, v]) => ({
  code: v.code,
  name: v.name,
}));

const DROPDOWN_Z_INDEX = 9999;
const DROPDOWN_EDGE_PADDING = 12;
const DROPDOWN_MAX_WIDTH = 320;
const DROPDOWN_MIN_WIDTH = 260;
const DROPDOWN_MAX_HEIGHT = "min(280px, 50vh)";
const RUN_TIME_MINUTES_STEP = 5;
const TIME_REGEX = /^([0-1]?[0-9]|2[0-3]):([0-5][05])$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TOAST = {
  LOAD_FAIL: "Failed to load configuration",
  SAVE_FAIL: "Failed to save",
  SAVE_SUCCESS: "Scheduled email report configuration saved.",
  NO_MERCHANT: "Merchant context missing.",
  NOT_AUTH: "Not authenticated.",
  TIME_FORMAT: "Use 24-hour time with minutes in steps of 5 (e.g. 09:00, 09:05, 14:30)",
};

// ---------------------------------------------------------------------------
// Auth & API helpers
// ---------------------------------------------------------------------------

function getAuthHeaders() {
  const token = localStorage.getItem("authToken");
  const merchantId = localStorage.getItem("merchantId");
  if (!token) return null;
  return {
    Authorization: token,
    "Content-Type": "application/json",
    ...(merchantId ? { "x-merchant-id": merchantId } : {}),
  };
}

function createClientId() {
  return `schedule-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getDefaultSchedule(clientId) {
  return {
    clientId: clientId ?? createClientId(),
    report_type_id: "pickup_pending",
    frequency: "DAILY",
    run_times_ist: ["09:00"],
    from_days_ago: 1,
    to_days_ago: 0,
    to_emails: [],
    cc_emails: null,
    bcc_emails: null,
    status_codes_override: null,
  };
}

/** Normalize API schedule to form shape; preserves clientId if present. */
function normalizeSchedule(apiSchedule) {
  const d = getDefaultSchedule();
  const s = apiSchedule || {};
  return {
    ...d,
    ...s,
    clientId: s.clientId ?? createClientId(),
    run_times_ist: Array.isArray(s.run_times_ist) ? s.run_times_ist : d.run_times_ist,
    to_emails: Array.isArray(s.to_emails) ? s.to_emails : d.to_emails,
    cc_emails: s.cc_emails != null ? (Array.isArray(s.cc_emails) ? s.cc_emails : []) : null,
    bcc_emails: s.bcc_emails != null ? (Array.isArray(s.bcc_emails) ? s.bcc_emails : []) : null,
    status_codes_override:
      s.status_codes_override != null
        ? (Array.isArray(s.status_codes_override) ? s.status_codes_override : [])
        : null,
  };
}

// ---------------------------------------------------------------------------
// Form components
// ---------------------------------------------------------------------------

function TagInput({ values, onChange, placeholder, validateItem, singularLabel }) {
  const [input, setInput] = useState("");
  const list = Array.isArray(values) ? values : [];
  const normalized = list.filter(Boolean);

  const add = (item) => {
    const trimmed = (item || "").trim();
    if (!trimmed) return;
    if (validateItem && !validateItem(trimmed)) return;
    if (normalized.includes(trimmed)) return;
    onChange([...normalized, trimmed]);
    setInput("");
  };

  const remove = (index) => {
    onChange(normalized.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input);
    } else if (e.key === "Backspace" && !input && normalized.length > 0) {
      remove(normalized.length - 1);
    }
  };

  const ariaLabel = singularLabel ? `Remove ${singularLabel}` : "Remove item";

  return (
    <div className="flex flex-wrap gap-2 p-2 sm:p-2.5 border border-gray-200 rounded-lg bg-gray-50/50 min-h-[42px] min-w-0 focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-400">
      {normalized.map((item, i) => (
        <span
          key={`${item}-${i}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-sm"
        >
          <span className="truncate max-w-[180px] sm:max-w-none">{item}</span>
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-blue-600 hover:text-blue-800 shrink-0 p-0.5 rounded touch-manipulation min-w-[28px] min-h-[28px] flex items-center justify-center"
            aria-label={ariaLabel}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => add(input)}
        placeholder={placeholder}
        className="flex-1 min-w-[80px] w-full bg-transparent border-0 outline-none text-sm text-gray-800 placeholder-gray-400"
      />
    </div>
  );
}

/** Run times (IST): HH:mm, minutes must be multiple of RUN_TIME_MINUTES_STEP. */
function RunTimesInput({ runTimesIst, onChange }) {
  const list = Array.isArray(runTimesIst) ? runTimesIst : [];
  const [input, setInput] = useState("");

  const add = () => {
    const t = input.trim();
    if (!t) return;
    if (!TIME_REGEX.test(t)) {
      toast.warning(TOAST.TIME_FORMAT);
      return;
    }
    if (list.includes(t)) return;
    onChange([...list, t].sort());
    setInput("");
  };

  const remove = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {list.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-800 text-sm font-mono"
          >
            {t}
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-slate-500 hover:text-slate-800 rounded p-0.5 touch-manipulation"
              aria-label={`Remove time ${t}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={`HH:mm, minutes in steps of ${RUN_TIME_MINUTES_STEP} (e.g. 09:00, 14:30)`}
          className="flex-1 min-w-0 w-full sm:w-auto sm:max-w-[120px] px-3 py-2 sm:py-1.5 border border-gray-200 rounded-lg text-sm font-mono focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30"
          aria-label="Run time (IST)"
        />
        <button type="button" onClick={add} className="w-full sm:w-auto px-3 py-2 sm:py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 touch-manipulation">
          Add time
        </button>
      </div>
    </div>
  );
}

function getDropdownPosition(anchorEl) {
  if (!anchorEl) return null;
  const rect = anchorEl.getBoundingClientRect();
  const maxW = Math.min(DROPDOWN_MAX_WIDTH, window.innerWidth - DROPDOWN_EDGE_PADDING * 2);
  const w = Math.min(rect.width, maxW);
  const left = Math.max(
    DROPDOWN_EDGE_PADDING,
    Math.min(rect.left, window.innerWidth - w - DROPDOWN_EDGE_PADDING)
  );
  return {
    left,
    top: rect.bottom + 4,
    width: w,
    minWidth: Math.min(rect.width, DROPDOWN_MIN_WIDTH),
  };
}

// Multi-select for order status: show names, store/send codes (OrderBusinessStatusEnum)
function StatusCodesMultiSelect({ selectedCodes, onChange, id }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [anchor, setAnchor] = useState(null);
  const [position, setPosition] = useState(null);
  const dropdownRef = useRef(null);

  const codes = Array.isArray(selectedCodes) ? selectedCodes : [];
  const filtered = STATUS_OPTIONS.filter((o) =>
    o.name.toLowerCase().includes((search || "").toLowerCase())
  );

  const toggle = (code) => {
    if (codes.includes(code)) {
      onChange(codes.filter((c) => c !== code));
    } else {
      onChange([...codes, code]);
    }
  };

  const selectAll = () => {
    const toAdd = filtered.filter((o) => !codes.includes(o.code)).map((o) => o.code);
    onChange([...new Set([...codes, ...toAdd])]);
  };

  const clearAll = () => {
    const toRemove = new Set(filtered.map((o) => o.code));
    onChange(codes.filter((c) => !toRemove.has(c)));
  };

  // Update dropdown position on scroll/resize so it stays under the trigger
  useEffect(() => {
    if (!open || !anchor) {
      if (!open) setPosition(null);
      return;
    }
    const updatePosition = () => setPosition(getDropdownPosition(anchor));
    updatePosition();
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && anchor && !anchor.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, anchor]);

  const dropdownStyle = position ?? (anchor ? getDropdownPosition(anchor) : null);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 min-w-0">
        {codes.map((code) => {
          const opt = STATUS_OPTIONS.find((o) => o.code === code);
          const name = opt ? opt.name : code;
          return (
            <span
              key={code}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-sm"
            >
              <span className="truncate max-w-[200px] sm:max-w-[280px]">{name}</span>
              <button
                type="button"
                onClick={() => onChange(codes.filter((c) => c !== code))}
                className="text-blue-600 hover:text-blue-800 shrink-0 p-0.5 rounded touch-manipulation"
                aria-label={`Remove ${name}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          );
        })}
      </div>
      <div ref={setAnchor} className="relative w-full min-w-0">
        <button
          type="button"
          id={id}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Select order statuses (optional)"
          className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-left bg-white hover:bg-gray-50 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 min-h-[42px] touch-manipulation"
        >
          <span className="text-gray-500 truncate">Select order statuses (optional)</span>
          <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
        </button>
        {open &&
          anchor &&
          dropdownStyle &&
          createPortal(
            <div
              ref={dropdownRef}
              role="listbox"
              aria-multiselectable="true"
              aria-label="Order status filter"
              className="fixed flex flex-col rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden"
              style={{
                zIndex: DROPDOWN_Z_INDEX,
                left: dropdownStyle.left,
                top: dropdownStyle.top,
                width: dropdownStyle.width,
                minWidth: dropdownStyle.minWidth,
                maxHeight: DROPDOWN_MAX_HEIGHT,
              }}
            >
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search status..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30"
                />
              </div>
              <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 text-xs">
                <button type="button" onClick={selectAll} className="text-blue-600 hover:underline">
                  Select all
                </button>
                <button type="button" onClick={clearAll} className="text-gray-500 hover:underline">
                  Clear
                </button>
              </div>
              <ul className="overflow-y-auto flex-1 p-1 min-h-0 max-h-[200px]">
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500">No matching status</li>
                ) : (
                  filtered.map((o) => {
                    const checked = codes.includes(o.code);
                    return (
                      <li key={o.code}>
                        <button
                          type="button"
                          onClick={() => toggle(o.code)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 rounded-md touch-manipulation"
                        >
                          <span className={`flex shrink-0 w-4 h-4 rounded border flex items-center justify-center ${checked ? "bg-blue-600 border-blue-600" : "border-gray-300"}`}>
                            {checked ? <Check className="w-2.5 h-2.5 text-white" /> : null}
                          </span>
                          <span className="truncate">{o.name}</span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
              <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
                {codes.length} selected
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}

function validateSchedule(schedule) {
  const errors = [];
  if (!schedule.to_emails?.length) {
    errors.push("At least one To email is required.");
  }
  const hasFrom = schedule.from_days_ago != null;
  const hasTo = schedule.to_days_ago != null;
  if (hasFrom && hasTo) {
    if (schedule.to_days_ago < 0) errors.push("To days ago must be ≥ 0.");
    if (schedule.from_days_ago < schedule.to_days_ago) {
      errors.push("From days ago must be ≥ To days ago.");
    }
  } else if (hasFrom || hasTo) {
    errors.push("Set both From days ago and To days ago, or leave both empty.");
  }
  return errors;
}

function LoadingView() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex items-center justify-center p-4 sm:p-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <DotLottieReact src={LOTTIE_LOADER} renderer="canvas" className="w-14 h-14 sm:w-16 sm:h-16" loop />
        <p className="text-sm text-gray-500 px-2">Loading scheduled report configuration…</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ScheduledEmailReportConfig() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const merchantId = localStorage.getItem("merchantId") || "";

  const fetchConfig = useCallback(async () => {
    if (!merchantId) return { schedules: [] };
    const headers = getAuthHeaders();
    if (!headers) return { schedules: [] };
    try {
      const { data } = await axios.get(API.merchantConfigScheduledEmailGet(merchantId), { headers });
      const value = data?.merchant_scheduled_email_report_configurations_value;
      const list = value?.schedules;
      return { schedules: Array.isArray(list) ? list : [] };
    } catch (e) {
      if (e.response?.status === 404) return { schedules: [] };
      throw e;
    }
  }, [merchantId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { schedules: list } = await fetchConfig();
      setSchedules(list.map(normalizeSchedule));
      if (list.length > 0) setExpandedId(0);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || TOAST.LOAD_FAIL;
      toast.error(msg);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [fetchConfig]);

  useEffect(() => {
    load();
  }, [load]);

  const updateSchedule = (index, patch) => {
    setSchedules((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const addSchedule = () => {
    setSchedules((prev) => [...prev, getDefaultSchedule()]);
    setExpandedId(schedules.length);
  };

  const removeSchedule = (index) => {
    setSchedules((prev) => prev.filter((_, i) => i !== index));
    setExpandedId((id) => (id === index ? null : id > index ? id - 1 : id));
  };

  const handleSave = async () => {
    if (!merchantId) {
      toast.error(TOAST.NO_MERCHANT);
      return;
    }
    const headers = getAuthHeaders();
    if (!headers) {
      toast.error(TOAST.NOT_AUTH);
      return;
    }
    const allErrors = [];
    schedules.forEach((s, i) => {
      validateSchedule(s).forEach((err) => allErrors.push(`Schedule ${i + 1}: ${err}`));
    });
    if (allErrors.length > 0) {
      toast.error(allErrors[0]);
      return;
    }

    const payload = {
      merchant_id: merchantId,
      merchant_configuration_key: CONFIG_KEY,
      merchant_scheduled_email_report_configurations_value: {
        schedules: schedules.map((s) => ({
          report_type_id: s.report_type_id,
          frequency: s.frequency,
          to_emails: s.to_emails || [],
          run_times_ist: s.run_times_ist?.length ? s.run_times_ist : null,
          from_days_ago: s.from_days_ago ?? null,
          to_days_ago: s.to_days_ago ?? null,
          cc_emails: s.cc_emails?.length ? s.cc_emails : null,
          bcc_emails: s.bcc_emails?.length ? s.bcc_emails : null,
          status_codes_override: s.status_codes_override?.length ? s.status_codes_override : null,
        })),
      },
    };

    setSaving(true);
    try {
      await axios.put(API.merchantConfigScheduledEmailPut(), payload, { headers });
      toast.success(TOAST.SAVE_SUCCESS);
      load();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || TOAST.SAVE_FAIL;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingView />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 px-3 py-4 sm:p-6 overflow-x-hidden">
      <div className="max-w-6xl mx-auto w-full min-w-0">
        {/* Page header */}
        <div className="mb-5 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Scheduled Email Reports</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            Configure status-history reports to be sent automatically by email. Column selection is managed in Report Configuration.
          </p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/80 overflow-hidden">
          <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-2.5 sm:py-3 bg-slate-800 text-white">
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300 shrink-0" />
              <span className="text-sm sm:text-base font-semibold truncate">Report schedules</span>
            </div>
            <button
              type="button"
              onClick={addSchedule}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 transition-colors shrink-0 touch-manipulation"
            >
              <Plus className="w-4 h-4" />
              Add schedule
            </button>
          </div>

          <div className="p-3 sm:p-5 md:p-6">
            {schedules.length === 0 ? (
              <div className="py-8 sm:py-12 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 px-4">
                <FileBarChart2 className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium text-sm sm:text-base">No scheduled reports</p>
                <p className="mt-1 text-xs sm:text-sm text-gray-500 max-w-sm mx-auto">
                  Add a schedule to receive automated status-history reports by email at your chosen times.
                </p>
                <button
                  type="button"
                  onClick={addSchedule}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium rounded-lg w-full sm:w-auto touch-manipulation"
                >
                  <Plus className="w-4 h-4" />
                  Add your first schedule
                </button>
              </div>
            ) : (
              <ul className="space-y-3 sm:space-y-4" role="list">
                {schedules.map((schedule, index) => {
                  const reportType = REPORT_TYPES.find((r) => r.id === schedule.report_type_id) || REPORT_TYPES[0];
                  const frequency = FREQUENCIES.find((f) => f.id === schedule.frequency) || FREQUENCIES[0];
                  const isExpanded = expandedId === index;

                  return (
                    <li
                      key={schedule.clientId}
                      className="border border-gray-200 rounded-xl bg-gray-50/30 overflow-hidden w-full min-w-0"
                    >
                      {/* Summary row – always visible */}
                      <button
                        type="button"
                        onClick={() => setExpandedId(isExpanded ? null : index)}
                        className="w-full flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 py-3 text-left hover:bg-gray-100/50 transition-colors min-w-0 touch-manipulation"
                      >
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-200 text-slate-800 text-xs sm:text-sm font-medium shrink-0">
                            {reportType.label}
                          </span>
                          <span className="text-xs sm:text-sm text-gray-500 shrink-0">{frequency.label}</span>
                          {Array.isArray(schedule.run_times_ist) && schedule.run_times_ist.length > 0 && (
                            <span className="text-xs sm:text-sm text-gray-500 flex items-center gap-1 shrink-0">
                              <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                              <span className="truncate">{schedule.run_times_ist.join(", ")} IST</span>
                            </span>
                          )}
                          {Array.isArray(schedule.to_emails) && schedule.to_emails.length > 0 && (
                            <span className="text-xs sm:text-sm text-gray-500 truncate min-w-0 max-w-[120px] sm:max-w-[200px]" title={schedule.to_emails.join(", ")}>
                              → {schedule.to_emails.length} recipient(s)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSchedule(index);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                            aria-label="Delete schedule"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {/* Expanded form */}
                      {isExpanded && (
                        <div className="px-3 sm:px-4 pb-4 pt-0 border-t border-gray-200 bg-white/50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                            {/* Report type */}
                            <div className="min-w-0">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                Report type
                              </label>
                              <select
                                value={schedule.report_type_id}
                                onChange={(e) => updateSchedule(index, { report_type_id: e.target.value })}
                                className="w-full min-w-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 touch-manipulation"
                              >
                                {REPORT_TYPES.map((r) => (
                                  <option key={r.id} value={r.id}>
                                    {r.label}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                                <Info className="w-3 h-3 shrink-0" />
                                {reportType.description}
                              </p>
                            </div>

                            {/* Frequency */}
                            <div className="min-w-0">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                Frequency
                              </label>
                              <select
                                value={schedule.frequency}
                                onChange={(e) => updateSchedule(index, { frequency: e.target.value })}
                                className="w-full min-w-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 touch-manipulation"
                              >
                                {FREQUENCIES.map((f) => (
                                  <option key={f.id} value={f.id}>
                                    {f.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Run times IST */}
                            <div className="md:col-span-2 min-w-0">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                <Clock className="w-3.5 h-3.5 inline mr-1" />
                                Run times (IST, 24-hour; minutes in steps of 5)
                              </label>
                              <RunTimesInput
                                runTimesIst={schedule.run_times_ist}
                                onChange={(run_times_ist) => updateSchedule(index, { run_times_ist })}
                              />
                            </div>

                            {/* Date range */}
                            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 sm:gap-4 md:col-span-2">
                              <div className="w-full sm:w-auto min-w-0">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                  <Calendar className="w-3.5 h-3.5 inline mr-1" />
                                  From days ago
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  value={schedule.from_days_ago ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                    updateSchedule(index, { from_days_ago: isNaN(v) ? null : v });
                                  }}
                                  placeholder="e.g. 1"
                                  className="w-full sm:w-28 min-w-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                                />
                              </div>
                              <div className="w-full sm:w-auto min-w-0">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                  To days ago
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  value={schedule.to_days_ago ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                    updateSchedule(index, { to_days_ago: isNaN(v) ? null : v });
                                  }}
                                  placeholder="0"
                                  className="w-full sm:w-28 min-w-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
                                />
                              </div>
                              <p className="text-xs text-gray-500 w-full sm:w-auto">Report window: run date minus these days (inclusive). Leave empty for default.</p>
                            </div>

                            {/* To emails */}
                            <div className="md:col-span-2 min-w-0">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                <Mail className="w-3.5 h-3.5 inline mr-1" />
                                To emails <span className="text-red-500">*</span>
                              </label>
                              <TagInput
                                values={schedule.to_emails}
                                onChange={(to_emails) => updateSchedule(index, { to_emails })}
                                placeholder="Add email, then Enter or comma"
                                validateItem={(v) => EMAIL_REGEX.test(v)}
                                singularLabel="email"
                              />
                            </div>

                            {/* CC / BCC */}
                            <div className="min-w-0">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                CC emails
                              </label>
                              <TagInput
                                values={schedule.cc_emails}
                                onChange={(cc_emails) => updateSchedule(index, { cc_emails: cc_emails.length ? cc_emails : null })}
                                placeholder="Optional"
                                validateItem={(v) => EMAIL_REGEX.test(v)}
                                singularLabel="email"
                              />
                            </div>
                            <div className="min-w-0">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                BCC emails
                              </label>
                              <TagInput
                                values={schedule.bcc_emails}
                                onChange={(bcc_emails) => updateSchedule(index, { bcc_emails: bcc_emails.length ? bcc_emails : null })}
                                placeholder="Optional"
                                validateItem={(v) => EMAIL_REGEX.test(v)}
                                singularLabel="email"
                              />
                            </div>

                            {/* Status filter: show names (OrderBusinessStatusEnum), send codes to API */}
                            <div className="md:col-span-2 min-w-0">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                Order status filter (optional)
                              </label>
                              <StatusCodesMultiSelect
                                id={`status-codes-${index}`}
                                selectedCodes={schedule.status_codes_override || []}
                                onChange={(status_codes_override) =>
                                  updateSchedule(index, {
                                    status_codes_override: status_codes_override?.length ? status_codes_override : null,
                                  })
                                }
                              />
                              <p className="mt-1 text-xs text-gray-500">Leave empty to use report-type defaults.</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Save */}
            {schedules.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto touch-manipulation min-h-[44px]"
                >
                  {saving ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save configuration
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
